// React
import React, { useContext, useEffect, useMemo, useState } from "react";

// MUI Core
import {
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import { useHistory } from "react-router-dom";
import { makeStyles } from 'tss-react/mui';

// Context e Hooks
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from '../../hooks/useSettings';

// Tradução
import { i18n } from "../../translate/i18n";

// Componentes
import Page from "../../components/Page";
import ToastError from "../../toast/error/toastError";
import Chart from "./Chart";

// MUI Icons
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import TimelineIcon from '@mui/icons-material/Timeline';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import ReplayIcon from '@mui/icons-material/Replay';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';

// Widgets do dashboard
import KpiCard from './widgets/KpiCard';
import CausesWidget from './widgets/CausesWidget';
import ChannelsWidget from './widgets/ChannelsWidget';
import RatingDistribution from './widgets/RatingDistribution';
import AttendanceTimeBuckets from './widgets/AttendanceTimeBuckets';
import UserRanking from './widgets/UserRanking';

// MUI Lab e X-Date-Pickers
import LoadingButton from '@mui/lab/LoadingButton';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Shortcuts de range de data — atalhos pra os filtros mais usados. Cada um
// devolve { start, end } como dayjs ao ser aplicado. Período é sempre
// inclusivo nos dois lados (server faz startOfDay/endOfDay).
const DATE_SHORTCUTS = [
  { key: 'today', label: 'Hoje', range: () => ({ start: dayjs(), end: dayjs() }) },
  {
    key: 'yesterday',
    label: 'Ontem',
    range: () => {
      const y = dayjs().subtract(1, 'day');
      return { start: y, end: y };
    },
  },
  {
    key: '7days',
    label: 'Últimos 7 dias',
    range: () => ({ start: dayjs().subtract(6, 'day'), end: dayjs() }),
  },
  {
    key: '30days',
    label: 'Últimos 30 dias',
    range: () => ({ start: dayjs().subtract(29, 'day'), end: dayjs() }),
  },
  {
    key: 'this_month',
    label: 'Este mês',
    range: () => ({ start: dayjs().startOf('month'), end: dayjs() }),
  },
  {
    key: 'last_month',
    label: 'Mês passado',
    range: () => {
      const prev = dayjs().subtract(1, 'month');
      return { start: prev.startOf('month'), end: prev.endOf('month') };
    },
  },
];

// Compara o range atual com o de um shortcut pra destacar o chip ativo.
// Tolerância em dia (startOf('day')) pra ignorar diferenças de hora.
const isShortcutActive = (shortcut, currentStart, currentEnd) => {
  const { start, end } = shortcut.range();
  return (
    dayjs(currentStart).startOf('day').isSame(start.startOf('day'), 'day') &&
    dayjs(currentEnd).startOf('day').isSame(end.startOf('day'), 'day')
  );
};

// Sentinelas imutáveis usadas como fallback dos breakdowns. Manter a mesma
// referência permite que os widgets memoizados detectem "props inalteradas"
// e pulem o re-render quando os dados ainda não chegaram.
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});
const EMPTY_RATING = Object.freeze([0, 0, 0, 0, 0]);

// Serviço API
import api from "../../services/api";

// Data Manipulation
import dayjs from "dayjs";

// Estilos
const useStyles = makeStyles()((theme) => ({
  root: {
    marginTop: 65,
    marginBottom: 65,
    minHeight: 'calc(100vh - 130px)'
  },
  paper: {
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    padding: 0
  },
  datePicker: {
    '& .MuiOutlinedInput-input': {
      padding: '8.5px 14px'
    }
  }
}));

// Componente Dashboard
const Dashboard = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { themeStretch } = useSettings();

  // Estados de carregamento
  const [loading, setLoading] = useState(true);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  // Estados de dados
  const [, setTickets] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [users, setUsers] = useState([]);
  const [, setQueues] = useState([]);
  const [metrics, setMetrics] = useState(null); // payload de /dashboard/metrics
  const [ticketsTimeStart, setTicketsTimeStart] = useState('');
  const [ticketsTimeEnd, setTicketsTimeEnd] = useState('');
  const [dateTicketsStart, setDateTicketsStart] = useState(dayjs());
  const [dateTicketsEnd, setDateTicketsEnd] = useState(dayjs());
  const [ticketsByStatus, setTicketsByStatus] = useState({ pending: 0, open: 0, closed: 0 });
  const [companyPeriods, setCompanyPeriods] = useState([]);


  // Empresa 2 + perfil "user" não acessa o Dashboard — redireciona pra Tickets.
  // Usa companyId/profile do contexto (não o localStorage, que pode ficar
  // defasado) e comparação estrita pra fechar o acesso direto por URL.
  useEffect(() => {
    if (!user?.companyId) return;
    if (Number(user.companyId) === 2 && user.profile === 'user') {
      history.push('/tickets');
    }
  }, [user]);

  // Buscar períodos ativos da empresa (uma vez)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings-company");
        const pow = data?.findPeriodOfWork;
        if (!pow) return;
        const now = new Date();
        const currentMin = now.getHours() * 60 + now.getMinutes();
        const active = [];
        for (const key of ['morning', 'afternoon', 'night']) {
          if (!pow[key]) continue;
          try {
            const { start, end } = JSON.parse(pow[key]);
            if (!start || !end || (start === '00:00' && end === '00:00')) continue;
            const [sh, sm] = start.split(':').map(Number);
            const [eh, em] = end.split(':').map(Number);
            const s = sh * 60 + sm;
            const e = eh * 60 + em;
            if (s < e ? (currentMin >= s && currentMin < e) : (currentMin >= s || currentMin < e)) {
              active.push(key);
            }
          } catch { }
        }
        setCompanyPeriods(active);
      } catch { }
    })();
  }, []);

  // Calcular status de cada usuário (dayoff/offshift/active)
  const usersWithStatus = useMemo(() => {
    if (!users?.length) return [];
    const todayDow = new Date().getDay();
    const todayDate = new Date().toISOString().split('T')[0];
    return users.map(usr => {
      const daysOff = usr.daysOff ? usr.daysOff.split(',').map(Number) : [];
      const extraDaysOff = usr.extraDaysOff ? usr.extraDaysOff.split(',') : [];
      const userPeriods = usr.period ? usr.period.split(',') : [];

      if (daysOff.includes(todayDow) || extraDaysOff.includes(todayDate)) {
        return { ...usr, _status: 'dayoff' };
      }
      if (userPeriods.length > 0 && companyPeriods.length > 0) {
        if (!companyPeriods.some(p => userPeriods.includes(p))) {
          return { ...usr, _status: 'offshift' };
        }
      }
      return { ...usr, _status: 'active' };
    });
  }, [users, companyPeriods]);

  // Função para buscar todos os dados. Aceita argumentos opcionais pra
  // permitir disparar com datas novas antes do re-render (shortcuts).
  const fetchAllData = async (forceStart, forceEnd) => {
    try {
      setLoading(true);

      const startDay = forceStart || dateTicketsStart;
      const endDay = forceEnd || dateTicketsEnd;

      // Manda só a parte da data (YYYY-MM-DD) pro backend. Ele aplica
      // startOfDay/endOfDay na timezone do servidor — sem ambiguidade.
      const formatDateStart = dayjs(startDay).format("YYYY-MM-DD");
      const formatDateEnd = dayjs(endDay).format("YYYY-MM-DD");

      if (formatDateEnd === ticketsTimeEnd && formatDateStart === ticketsTimeStart) {
        setLoading(false);
        return;
      }

      setTicketsTimeEnd(formatDateEnd);
      setTicketsTimeStart(formatDateStart);

      const params = { dateStart: formatDateStart, dateEnd: formatDateEnd };

      const fetchTickets = async () => {
        setLoadingTickets(true);
        try {
          const { data } = await api.get("/tickets-dashboard", { params });
          setTickets(data.tickets);
          setChartData(data.relatory);
          setTicketsByStatus(data.count);
        } catch (err) {
          ToastError(err);
        } finally {
          setLoadingTickets(false);
        }
      };

      const fetchQueues = async () => {
        setLoadingQueue(true);
        try {
          const { data } = await api.get("/queue-dashboard", { params });
          setQueues(data);
        } catch (err) {
          ToastError(err);
        } finally {
          setLoadingQueue(false);
        }
      };

      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const { data } = await api.get("/users-dashboard", { params });
          setUsers(data.users);
        } catch (err) {
          ToastError(err);
        } finally {
          setLoadingUsers(false);
        }
      };

      const fetchMetrics = async () => {
        setLoadingMetrics(true);
        try {
          const { data } = await api.get("/dashboard/metrics", { params });
          setMetrics(data);
        } catch (err) {
          ToastError(err);
        } finally {
          setLoadingMetrics(false);
        }
      };

      await Promise.allSettled([fetchTickets(), fetchQueues(), fetchUsers(), fetchMetrics()]);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Destructuring para simplificar o acesso às contagens de tickets
  const {
    pending_count: pending,
    open_count: open,
    closed_count: closed,
    total_count
  } = ticketsByStatus;

  // Helpers de formatação. Centralizados aqui pra os widgets de KPI mostrarem
  // o mesmo valor que aparece em hover/tooltip sem reimplementar a lógica.
  const formatSeconds = (s) => {
    if (!s || s <= 0) return "0min";
    const min = Math.floor(s / 60);
    if (min < 60) return `${min}min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h${m.toString().padStart(2, "0")}`;
  };

  const cur = metrics?.current;
  const prev = metrics?.previous;
  // live: snapshot de pending/open AGORA (independente do período do filtro).
  // É o que faz sentido pra "carga atual do time".
  const live = metrics?.live;

  // Função para obter a saudação com base na hora atual
  const greetingMessage = () => {
    const greetings = [
      `${i18n.t("salutation.gooddawn")}`,
      `${i18n.t("salutation.goodmorning")}`,
      `${i18n.t("salutation.goodafternoon")}`,
      `${i18n.t("salutation.goodnight")}`
    ];
    let h = new Date().getHours();
    return greetings[(h / 6) >> 0];
  }

  // Funções para alterar as datas do filtro
  const changeDateStart = e => {
    setDateTicketsStart(e);
  }

  const changeDateEnd = e => {
    setDateTicketsEnd(e);
  }

  // Aplica um shortcut e dispara o reload. Os dois setters disparam re-render
  // mas o fetchAllData lê dos states atualizados na próxima frame.
  const applyShortcut = (shortcut) => {
    const { start, end } = shortcut.range();
    setDateTicketsStart(start);
    setDateTicketsEnd(end);
    // Dispara o fetch passando explicitamente os novos valores — sem isso a
    // closure de fetchAllData usaria os states velhos.
    setTimeout(() => fetchAllData(start, end), 0);
  };

  return (
    <Box className={classes.root}>
      <Page title="Dashboard">
        <Container maxWidth={themeStretch ? false : 'xl'} sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 2.5 }}>
            <Grid item xs={12}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: -0.5,
                  fontSize: { xs: 22, sm: 28, md: 32 },
                  mb: 0.5,
                }}
              >
                {`${greetingMessage()}, ${user.name}!`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acompanhe os indicadores de atendimento no período selecionado.
              </Typography>
            </Grid>

            {/* Filtro de datas com shortcuts — empilha no mobile */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, border: 1, borderColor: 'divider' }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={{ xs: 1.5, md: 2 }}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
                      <DesktopDatePicker
                        className={classes.datePicker}
                        label="De"
                        slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 160 } } } }}
                        inputFormat="DD/MM/YYYY"
                        value={dayjs(dateTicketsStart)}
                        maxDate={dayjs(dateTicketsEnd)}
                        onChange={(newValue) => changeDateStart(newValue)}
                      />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'pt-br'}>
                      <DesktopDatePicker
                        className={classes.datePicker}
                        label="Até"
                        slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 160 } } } }}
                        inputFormat="DD/MM/YYYY"
                        value={dayjs(dateTicketsEnd)}
                        minDate={dayjs(dateTicketsStart)}
                        maxDate={dayjs(new Date())}
                        onChange={(newValue) => changeDateEnd(newValue)}
                      />
                    </LocalizationProvider>
                  </Box>

                  {/* Shortcuts — atalhos rápidos pros ranges mais comuns */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                    {DATE_SHORTCUTS.map((sc) => {
                      const active = isShortcutActive(sc, dateTicketsStart, dateTicketsEnd);
                      return (
                        <Chip
                          key={sc.key}
                          label={sc.label}
                          size="small"
                          variant={active ? 'filled' : 'outlined'}
                          color={active ? 'primary' : 'default'}
                          onClick={() => applyShortcut(sc)}
                          sx={{
                            cursor: 'pointer',
                            height: 28,
                            fontSize: 12,
                            fontWeight: active ? 600 : 500,
                          }}
                        />
                      );
                    })}
                  </Box>

                  <LoadingButton
                    startIcon={<ManageSearchIcon />}
                    loading={loading}
                    variant="contained"
                    onClick={() => fetchAllData()}
                    size="medium"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      flexShrink: 0,
                      minWidth: 130,
                    }}
                  >
                    Atualizar
                  </LoadingButton>
                </Stack>
              </Paper>
            </Grid>

            {/* Linha 1 — contagens absolutas com Δ vs período anterior */}
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="Total no período"
                value={cur?.counts?.total ?? Number(total_count) ?? 0}
                previousValue={prev?.counts?.total}
                icon={<TimelineIcon fontSize="small" />}
                color="primary"
                loading={loadingMetrics}
                hint="Tickets criados no intervalo selecionado"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="Pendentes agora"
                value={live?.pending ?? pending ?? 0}
                // Snapshot ao vivo — sem comparativo de período (não faz sentido)
                icon={<HourglassBottomIcon fontSize="small" />}
                color="warning"
                betterWhen="lower"
                loading={loadingMetrics}
                hint="Tickets aguardando atendente — estado atual, não filtra pelo período"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="Em atendimento agora"
                value={live?.open ?? open ?? 0}
                icon={<RecordVoiceOverIcon fontSize="small" />}
                color="info"
                loading={loadingMetrics}
                hint="Conversas abertas com atendente — estado atual, não filtra pelo período"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="Fechados"
                value={cur?.counts?.closed ?? closed ?? 0}
                previousValue={prev?.counts?.closed}
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                color="success"
                loading={loadingMetrics}
                hint="Tickets finalizados no período"
              />
            </Grid>

            {/* Linha 2 — eficiência e qualidade do atendimento */}
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="Tempo de resposta"
                value={formatSeconds(cur?.times?.avgResponseSeconds)}
                previousValue={prev?.times?.avgResponseSeconds}
                icon={<QuestionAnswerIcon fontSize="small" />}
                color="info"
                betterWhen="lower"
                loading={loadingMetrics}
                hint="TMR — tempo médio entre criação do ticket e a primeira resposta enviada"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="Tempo de espera"
                value={formatSeconds(cur?.times?.avgWaitSeconds)}
                previousValue={prev?.times?.avgWaitSeconds}
                icon={<HourglassEmptyIcon fontSize="small" />}
                color="warning"
                betterWhen="lower"
                loading={loadingMetrics}
                hint="TME — tempo médio que o ticket fica em pendente até um atendente aceitar"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="CSAT"
                value={(cur?.quality?.csat ?? 0).toFixed(1)}
                suffix="/ 5"
                previousValue={prev?.quality?.csat}
                icon={<StarHalfIcon fontSize="small" />}
                color="success"
                loading={loadingMetrics}
                hint={`Nota média das avaliações · ${cur?.quality?.ratedPct ?? 0}% dos fechados foram avaliados`}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <KpiCard
                title="Taxa de reabertura"
                value={`${cur?.quality?.reopenPct ?? 0}`}
                suffix="%"
                previousValue={prev?.quality?.reopenPct}
                icon={<ReplayIcon fontSize="small" />}
                color="error"
                betterWhen="lower"
                loading={loadingMetrics}
                hint={`% dos tickets que voltaram a ser abertos · ${cur?.quality?.reopenEvents ?? 0} reaberturas no período`}
              />
            </Grid>

            {/* Linha 3 — quebras analíticas */}
            <Grid item xs={12} md={6}>
              <CausesWidget
                data={cur?.breakdowns?.byCause ?? EMPTY_ARRAY}
                totalClosed={cur?.counts?.closed || 0}
                loading={loadingMetrics}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <AttendanceTimeBuckets
                buckets={cur?.times?.buckets ?? EMPTY_OBJECT}
                avgSeconds={cur?.times?.avgHandlingSeconds || cur?.times?.avgAttendanceSeconds || 0}
                loading={loadingMetrics}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RatingDistribution
                distribution={cur?.quality?.ratingDistribution ?? EMPTY_RATING}
                csat={cur?.quality?.csat || 0}
                ratedPct={cur?.quality?.ratedPct || 0}
                loading={loadingMetrics}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChannelsWidget
                data={cur?.breakdowns?.byChannel ?? EMPTY_ARRAY}
                loading={loadingMetrics}
              />
            </Grid>

            {/* Ranking ocupa toda a largura agora — visual mais "leaderboard" */}
            <Grid item xs={12}>
              <UserRanking
                loading={loadingTickets || loadingQueue || loadingUsers}
                users={usersWithStatus}
                currentUserId={user?.id}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} className={classes.paper}>
                <Chart chartData={chartData} />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Page>
    </Box>
  );
};

export default Dashboard;